import { ReactThreeFiber, extend } from '@react-three/fiber';
import * as React from 'react';
import * as THREE from 'three';

import { MeshLineGeometry, MeshLineMaterial } from 'meshline';
import { useWindowSize } from 'react-use';

extend({ MeshLineGeometry, MeshLineMaterial });

type Props = JSX.IntrinsicElements['mesh'] & {
    threshold?: number;
    color?: ReactThreeFiber.Color;
    isDrawed?: boolean;
};

export const OutlineEdge = React.forwardRef(
    (
        {
            children,
            geometry,
            threshold = 15,
            color = 'black',
            isDrawed,
        }: Props,
        fref: React.ForwardedRef<THREE.Mesh | null>
    ) => {
        const { width, height } = useWindowSize();
        const [lineWidth, setLineWidth] = React.useState<number>(0.01);
        const [isDrawing, setDrawing] = React.useState<boolean>(true);
        React.useLayoutEffect(() => {
            setLineWidth(0.014);
        }, [width, height]);
        const ref = React.useRef<THREE.Mesh>(null!);
        const lineRef = React.useRef<MeshLineGeometry | null>(null);
        React.useLayoutEffect(() => {
            const parent = ref.current.parent as THREE.Mesh;
            if (parent) {
                const geom = geometry || parent.geometry;
                if (
                    geom !== ref.current.userData.currentGeom ||
                    threshold !== ref.current.userData.currentThreshold
                ) {
                    ref.current.userData.currentGeom = geom;
                    ref.current.userData.currentThreshold = threshold;
                    const geo = new THREE.EdgesGeometry(geom, threshold);
                    const position = geo.getAttribute('position').array;

                    const vertices: { x: number; y: number; z: number }[] = [];

                    for (let i = 0; i < position.length; i += 3) {
                        //  const counter = i / position.length;
                        if (i !== 3) {
                            if (isDrawed) {
                                vertices.push({
                                    x: position[i],
                                    y: position[i + 1],
                                    z: position[i + 2],
                                });
                                vertices.push({
                                    x: position[i],
                                    y: position[i + 1],
                                    z: position[i + 2],
                                });
                            } else {
                                vertices.push({
                                    x: position[i],
                                    y: position[i + 1],
                                    z: position[i + 2],
                                });
                                vertices.push({
                                    x: position[i],
                                    y: position[i + 1],
                                    z: position[i + 2],
                                });
                            }
                        }
                    }
                    if (
                        vertices &&
                        Array.isArray(vertices) &&
                        vertices.length > 0
                    ) {
                        const horizontalSort = vertices.sort(
                            (e, d) => e.x - d.x
                        );
                        const verticalSort = vertices.sort((e, d) => e.y - d.y);
                        if (horizontalSort && verticalSort) {
                            lineRef.current?.setPoints([
                                new THREE.Vector2(
                                    horizontalSort[0].x,
                                    verticalSort[0].y
                                ),
                                new THREE.Vector2(
                                    horizontalSort[0].x,
                                    verticalSort[verticalSort.length - 1].y
                                ),
                                new THREE.Vector2(
                                    horizontalSort[horizontalSort.length - 1].x,
                                    verticalSort[verticalSort.length - 1].y
                                ),
                                new THREE.Vector2(
                                    horizontalSort[horizontalSort.length - 1].x,
                                    verticalSort[0].y
                                ),
                                new THREE.Vector2(
                                    horizontalSort[0].x,
                                    verticalSort[0].y
                                ),
                            ]);
                            setDrawing(true);
                        }
                    } else {
                        setDrawing(false);
                    }
                }
            }
        });
        React.useImperativeHandle(fref, () => ref.current);
        if (isDrawing) {
            return (
                <mesh ref={ref} raycast={() => null}>
                    {children || (
                        <>
                            <meshLineGeometry
                                ref={lineRef}
                                points={[0, 0, 0, 1, 0, 0]}
                            />
                            <meshLineMaterial
                                lineWidth={lineWidth}
                                color={color}
                            />
                        </>
                    )}
                </mesh>
            );
        }
        return <mesh ref={ref}>{children}</mesh>;
    }
);
